import { useLazyGetSessionsOpportunitiesQuery } from '@carboncopyco/data';
import type { SessionsOpportunity } from '@carboncopyco/data';
import { TextLargeParagraph } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDashboardAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useEffect } from 'react';
import { useDashboardProps } from '../../../DashboardContext';
import { MinimalTable } from '../../../MinimalTable';
import { TableEmptyState } from '../../../SessionBreakdownComponents';
import { ViewAllOpportunitiesModal } from './ViewAllOpportunitiesModal';
import { getCols } from './opportunitiesCols';

const cols = getCols();

export const OpportunitiesTable = ({ maxItems }: { maxItems?: number }) => {
  const [showViewAllModal, setShowViewAllModal] = useBoolean(false);
  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    planId,
  } = useDashboardProps();
  const [getSessionsOpportunities, { data, isLoading, error }] =
    useLazyGetSessionsOpportunitiesQuery();
  const { trackDashboardViewMoreClick } = useDashboardAnalytics();

  useEffect(() => {
    getSessionsOpportunities({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes:
        planId === 'environment'
          ? ['shared_presentation', 'managed_presentation', 'play']
          : ['managed_presentation', 'play'],
    });
  }, [
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    getSessionsOpportunities,
    planId,
  ]);

  return (
    <>
      <MinimalTable<SessionsOpportunity>
        header={
          <div className="flex items-center gap-2">
            <TextLargeParagraph
              color="text-ds-natural-600"
              label="Opportunities"
            />
            {!!data?.metadata.totalItems && (
              <TextLargeParagraph
                label={`(${data?.metadata.totalItems})`}
                color="text-ds-natural-300"
              />
            )}
          </div>
        }
        cols={cols}
        data={data?.items ?? []}
        isLoading={isLoading}
        error={error}
        maxItems={maxItems}
        onViewAll={() => {
          setShowViewAllModal.setTrue();
          trackDashboardViewMoreClick({
            dashboardSection: 'opportunities_table',
          });
        }}
        emptyState={
          <div className="flex flex-col items-center gap-2">
            <TableEmptyState />
          </div>
        }
      />
      {showViewAllModal && (
        <ViewAllOpportunitiesModal
          open={showViewAllModal}
          onClose={setShowViewAllModal.setFalse}
        />
      )}
    </>
  );
};
